import { useState, useEffect } from 'react'
import { X, FileText, Loader2 } from 'lucide-react'
import Markdown from 'react-markdown'
import { useMemoLinkStore } from '@/stores/memoLinkStore'
import type { LinkedMemo } from '@/services/memoBridge'

interface LinkedMemoPreviewProps {
  syncId: string
  title: string
  color: string
  onClose: () => void
}

export function LinkedMemoPreview({ syncId, title, color, onClose }: LinkedMemoPreviewProps) {
  const getMemo = useMemoLinkStore((s) => s.getMemo)
  const [memo, setMemo] = useState<LinkedMemo | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    getMemo(syncId)
      .then((m) => { if (!cancelled) { setMemo(m); setLoading(false) } })
      .catch(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [syncId, getMemo])

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/40 z-[100]" onClick={onClose} />

      {/* Sheet */}
      <div className="fixed inset-x-0 bottom-0 z-[100] bg-surface-primary rounded-t-2xl shadow-2xl max-h-[80dvh] flex flex-col pb-safe animate-in slide-in-from-bottom duration-200">
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 rounded-full bg-surface-tertiary" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2 border-b border-base">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
            <h3 className="text-sm font-semibold text-heading truncate">{title}</h3>
          </div>
          <button onClick={onClose} className="p-1.5 text-caption hover:text-heading rounded-lg">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-4 py-4">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-5 h-5 animate-spin text-caption" />
            </div>
          )}
          {!loading && !memo && (
            <div className="text-center py-12">
              <FileText className="w-8 h-8 mx-auto text-disabled mb-2" />
              <p className="text-sm text-caption">메모를 불러올 수 없습니다.</p>
            </div>
          )}
          {!loading && memo && (
            <>
              {memo.tags?.length > 0 && (
                <div className="flex gap-1.5 mb-3 flex-wrap">
                  {memo.tags.map((tag) => (
                    <span key={tag} className="text-xs px-2 py-0.5 rounded-full bg-primary-50 text-primary-600 dark:bg-primary-900/20 dark:text-primary-400">
                      #{tag}
                    </span>
                  ))}
                </div>
              )}
              <div className="prose prose-sm dark:prose-invert max-w-none">
                <Markdown>{memo.body}</Markdown>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  )
}
